import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthorizationStore } from '@stores/authorizationStore'
import { usePersonalSettingsStore } from '@stores/personalSettingsStore'
import api from '@/axios/api'

import { AppCookies } from '@models/helpers/cookies'
import { AxiosErrorHandler } from '@models/helpers/axiosErrorHandler'

import { type LanguageType } from '@models/entities/languages'
import {
  type IpItemType,
  type UserCompanyType,
  type UserSettingsType,
  type UserType
} from '@models/entities/users'
import {
  type SalePointPermissionsType,
  type UserPermissionsType
} from '@models/entities/permissions'
import {
  SecuritySettingsScreens,
  type SecuritySettingsScreenType
} from '@models/entities/securitySettings'
import {
  type PostUserInfoRequestPayloadDataType,
  type PostUserPermissionsRequestPayloadDataType,
  type InviteUserRequestPayloadDataType,
  type UpdateUserSettingsPayloadDataType,
  type UpdateIpRestrictionsPayloadDataType
} from '@models/requests/payloads'

import mocks from '@/dev/mocks'

export const useUserStore = defineStore('useUserStore', () => {
  const authStore = useAuthorizationStore()
  const personalSettingsStore = usePersonalSettingsStore()

  const user = ref<UserType | null>(null)
  const userList = ref<UserType[]>([])
  const isShownPersonalSettings = ref(false)
  const isShownCompanySettings = ref(false)
  const isShownApiSettings = ref(false)
  const isShownSecuritySettings = ref(false)
  const securitySettingsScreen = ref<SecuritySettingsScreenType>(SecuritySettingsScreens.MENU)

  const _getUserPermissions = computed<UserPermissionsType | null>(() => user.value?.user_permissions ?? null)

  const getUserLanguage = computed<LanguageType | null>(() => user.value?.lang ?? null)
  const getUserName = computed(() => user.value?.name ?? '')
  const getUserEmail = computed(() => user.value?.email ?? '')
  const getUserCompany = computed<UserCompanyType | null>(() => user.value?.company ?? null)
  const getUserRegisteredDate = computed(() => user.value?.registered_date ?? '')
  const getUserMFAStatus = computed(() => user.value?.login_mfa ?? false)
  const isOwnerUser = computed(() => !!getUserCompany.value)
  const isEnabledPortalIpWhitelist = computed(() => getUserCompany.value?.portal_ip_whitelist_enabled ?? false)
  const portalIpWhitelist = computed<IpItemType[]>(() => getUserCompany.value?.portal_ip_whitelist ?? [])
  const isEnabledApiIpWhitelist = computed(() => getUserCompany.value?.api_ip_whitelist_enabled ?? false)
  const apiIpWhitelist = computed<IpItemType[]>(() => getUserCompany.value?.api_ip_whitelist ?? [])

  const availableSalepoints = computed<SalePointPermissionsType[]>(() => _getUserPermissions.value?.salepoints ?? [])
  const isAvailableCreatingInvoicesAndPayouts = computed(() =>
    isOwnerUser.value || (_getUserPermissions.value?.permissions.creating_invoices_and_payouts ?? false))
  // TODO: Раскомментировать и применить после добавления логики выгрузки реестра
  // const isAvailableRegistryExport = computed(() =>
  //   isOwnerUser.value || (_getUserPermissions.value?.permissions.registry_export ?? false))
  const isAvailableManageApiSettings = computed(() =>
    isOwnerUser.value || (_getUserPermissions.value?.permissions.manage_api_settings ?? false))
  const isAvailableSettlementRequestCreating = computed(() => getUserMFAStatus.value)
  const isAvailableViewAnalytics = computed(() =>
    isOwnerUser.value || (_getUserPermissions.value?.permissions.view_analytics ?? false))
  const isAvailableViewFinance = computed(() =>
    isOwnerUser.value || (_getUserPermissions.value?.permissions.view_finance ?? false))

  const setShownPersonalSettings = (status: boolean = true) => {
    isShownPersonalSettings.value = status
  }

  const setShownCompanySettings = (status: boolean = true) => {
    isShownCompanySettings.value = status
  }

  const setShownApiSettings = (status: boolean = true) => {
    isShownApiSettings.value = status
  }

  const setShownSecuritySettings = (status: boolean = true) => {
    isShownSecuritySettings.value = status
  }

  const setSecuritySettingsScreen = (screen: SecuritySettingsScreenType) => {
    securitySettingsScreen.value = screen
  }

  const setInitUserPersonalSettings = (userSettings: UserSettingsType) => {
    personalSettingsStore.setTimeZone(userSettings.timezone || 'Etc/UTC', true)
    personalSettingsStore.setTimeFormat(userSettings.time_format, true)
    personalSettingsStore.setDateFormat(userSettings.date_format, true)
    personalSettingsStore.setDecimalsSeparatorNumberSettings(userSettings.number_format.decimals_separator, true)
    personalSettingsStore.setDigitsSeparatorNumberSettings(userSettings.number_format.digits_separator, true)
    personalSettingsStore.setIsLeftCurrencySettings(userSettings.currency_format.left_position, true)
    personalSettingsStore.setIsSignCurrencySettings(userSettings.currency_format.sign, true)
  }

  const getUserInfo = async (isInitial: boolean = false) => {
    if (import.meta.env.MODE === 'development') {
      user.value = mocks.user_mock
      authStore.setAuthorizationStatus(true)
      authStore.setCheckedAuthorizationStatus(true)
      setInitUserPersonalSettings(user.value!.settings)
    } else {
      await api.get('/personal-api/v1/user/')
        .then((response) => {
          if (response.data) {
            user.value = response.data

            if (user.value!.settings) {
              setInitUserPersonalSettings(user.value!.settings)
            }
          }

          if (isInitial) {
            authStore.setAuthorizationStatus(true)
            authStore.setCheckedAuthorizationStatus(true)
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogoutError) {
            authStore.setAuthorizationStatus(false)
          }
          if (isInitial) {
            authStore.setCheckedAuthorizationStatus(true)
          }
        })
    }
  }

  const postUserInfo = async (payloadData: PostUserInfoRequestPayloadDataType) => {
    if (import.meta.env.MODE === 'development') {
      return
    }

    const cookies = new AppCookies()

    await api.post('/personal-api/v1/user/', payloadData, {
      headers: {
        // @ts-ignore
        common: {
          'x-csrftoken': cookies.getCookie('csrftoken')
        }
      }
    })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
  }

  const getUserList = async () => {
    if (import.meta.env.MODE === 'development') {
      userList.value = mocks.user_list_mock
    } else {
      await api.get('/personal-api/v1/users/')
        .then((response) => {
          if (response.data) {
            userList.value = response.data
          }
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogoutError) {
            authStore.setAuthorizationStatus(false)
          }
        })
    }
  }

  const postUserPermissions = async (userId: number, payloadData: PostUserPermissionsRequestPayloadDataType) => {
    const cookies = new AppCookies()

    await api.post(`/personal-api/v1/users/permissions/${userId}/`, payloadData, {
      headers: {
        // @ts-ignore
        common: {
          'x-csrftoken': cookies.getCookie('csrftoken')
        }
      }
    })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
  }

  const inviteUser = async (payloadData: InviteUserRequestPayloadDataType) => {
    const cookies = new AppCookies()

    await api.post('/personal-api/v1/users/add/', payloadData, {
      headers: {
        // @ts-ignore
        common: {
          'x-csrftoken': cookies.getCookie('csrftoken')
        }
      }
    })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
  }

  const updateUserSettings = async (payloadData: UpdateUserSettingsPayloadDataType) => {
    const cookies = new AppCookies()

    await api.post('/personal-api/v1/user/update-settings/', payloadData, {
      headers: {
        // @ts-ignore
        common: {
          'x-csrftoken': cookies.getCookie('csrftoken')
        }
      }
    })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
  }

  const updateIpRestrictions = async (payloadData: UpdateIpRestrictionsPayloadDataType) => {
    const cookies = new AppCookies()

    await api.post('/personal-api/v1/user/ip-whitelists/', payloadData, {
      headers: {
        // @ts-ignore
        common: {
          'x-csrftoken': cookies.getCookie('csrftoken')
        }
      }
    })
      .then((response) => {
        if (response.data && !!user.value) {
          user.value.company = {
            ...user.value.company,
            ...response.data
          }
        }
      })
      .catch((error) => {
        const errorHandler = new AxiosErrorHandler(error)
        if (errorHandler.isLogoutError) {
          authStore.setAuthorizationStatus(false)
        }
      })
  }

  return {
    user,
    userList,
    isShownPersonalSettings,
    isShownCompanySettings,
    isShownApiSettings,
    isShownSecuritySettings,
    securitySettingsScreen,
    getUserLanguage,
    getUserName,
    getUserEmail,
    getUserRegisteredDate,
    getUserMFAStatus,
    isOwnerUser,
    isEnabledPortalIpWhitelist,
    portalIpWhitelist,
    isEnabledApiIpWhitelist,
    apiIpWhitelist,
    availableSalepoints,
    isAvailableCreatingInvoicesAndPayouts,
    // isAvailableRegistryExport,
    isAvailableManageApiSettings,
    isAvailableSettlementRequestCreating,
    isAvailableViewAnalytics,
    isAvailableViewFinance,
    setShownPersonalSettings,
    setShownCompanySettings,
    setShownApiSettings,
    setShownSecuritySettings,
    setSecuritySettingsScreen,
    getUserInfo,
    postUserInfo,
    getUserList,
    postUserPermissions,
    inviteUser,
    updateUserSettings,
    updateIpRestrictions
  }
})
